import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
  Alert,
  Box,
  Button,
  Chip,
} from '@mui/material';
import useApi from '../hooks/useApi';
import ProductSelectionsTable from './ProductSelectionsTable';
import SendHistoryResponseModal from './SendHistoryResponseModal';
import ConfirmDialog from './ConfirmDialog';
import { useEnrichedSendHistoryRows } from '../hooks/useEnrichedSendHistoryRows';
import { CATEGORY_PROGRAM_MAP } from '../utils/vmt';
import { normalizeSelectionBrands } from '../utils/brands';
import { buildCalendarDateMeta, buildDateMeta } from '../utils/dateUtils';
import { PROGRAM_CODE_MAP, resolveProgramCode } from '../utils/dataNormalizer';

const createDateSortComparator = (key) => (_value1, _value2, params1, params2) => {
  const left = params1?.row?.[key];
  const right = params2?.row?.[key];
  if (left === right) return 0;
  if (left == null) return -1;
  if (right == null) return 1;
  return left - right;
};

export const SEND_HISTORY_ADDITIONAL_COLUMNS = [
  {
    field: 'sendStatus',
    headerName: 'Send Status',
    width: 150,
    renderCell: (params) => {
      const value = params?.row?.sendStatus || params?.value || 'unknown';
      const color =
        value === 'success'
          ? 'success'
          : value === 'error'
            ? 'error'
            : value === 'pending'
              ? 'info'
              : 'default';
      return <Chip size="small" label={value} color={color} />;
    },
  },
  {
    field: 'sendTimestamp',
    headerName: 'Sent At',
    width: 200,
    type: 'dateTime',
    valueGetter: (params) => {
      const sortValue = params?.row?.sendTimestampSort;
      if (sortValue != null) {
        const date = new Date(sortValue);
        return Number.isNaN(date.getTime()) ? null : date;
      }
      return params?.row?.sendTimestampDisplay || params?.row?.sendTimestamp || '';
    },
    valueFormatter: (params) => params?.row?.sendTimestampDisplay || params?.value || '',
    renderCell: (params) => params?.row?.sendTimestampDisplay || params?.value || '',
    sortComparator: createDateSortComparator('sendTimestampSort'),
  },
];

const coalesce = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) return trimmed;
      continue;
    }
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
    if (value) return value;
  }
  return '';
};

const buildInstantDateFields = (rawValue) => {
  const meta = buildDateMeta(rawValue, {
    timeZone: 'America/Chicago',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  return {
    value: rawValue || '',
    display: meta.display || '',
    sort: meta.sort ?? null,
  };
};

const buildCalendarDateFields = (rawValue) => {
  const meta = buildCalendarDateMeta(rawValue);
  return {
    value: rawValue || '',
    display: meta.display || '',
    sort: meta.sort ?? null,
  };
};

/**
 * Take a raw send-history row (which mirrors the VMT payload) and coerce it into
 * the richer schema expected by ProductSelectionsTable (includes chip-friendly fields).
 */
const normalizeSendHistoryRow = (record) => {
  const brandSource =
    record.brands ||
    record.brandCodes ||
    record.programBrands ||
    (record.gmBrandCd ? [record.gmBrandCd] : []);
  const brands = normalizeSelectionBrands(brandSource);

  const selectionDateInfo = buildCalendarDateFields(
    coalesce(record.productSelectionDate, record.selectionDate)
  );
  const goLiveInfo = buildCalendarDateFields(coalesce(record.goLiveDate, record.effectiveDate));
  const effectiveInfo = buildCalendarDateFields(record.effectiveDate);
  const cancelInfo = buildCalendarDateFields(record.cancelDate);
  const billingStartInfo = buildCalendarDateFields(
    coalesce(record.billingStartDate, record.billingDate)
  );
  const billingEndInfo = buildCalendarDateFields(record.billingEndDate);
  const vendorProvidedInfo = buildCalendarDateFields(record.vendorProvidedDate);
  const terminationInfo = buildInstantDateFields(
    coalesce(record.terminationDate, record.isTerminatedDate, record.optOutDate, record.optOutDt)
  );
  const enrollmentInfo = buildInstantDateFields(coalesce(record.enrollmentDate, record.enrolledDt));
  const optOutInfo = buildInstantDateFields(coalesce(record.optOutDate, record.optOutDt));
  const sendTimestampInfo = buildInstantDateFields(record.sendTimestamp);

  return {
    ...record,
    programCode:
      record.programCode ||
      resolveProgramCode(record.programName) ||
      PROGRAM_CODE_MAP[record.programName] ||
      record.program ||
      record.programCd ||
      '',
    vendorCode: record.vendorCode || record.vendorCd || '',
    vendorName: record.vendorName || record.vendor || '',
    vendorInvoiceNumber:
      record.vendorInvoiceNumber || record.vendorInvoiceNbr || record.invoiceNumber || record.bac || '',
    categoryCode: record.categoryCode || record.categoryCd || '',
    categoryName: record.categoryName || record.programName || record.category || '',
    productBrandCode: record.productBrandCode || record.productCd || record.productBrandCd || '',
    type: record.type || record.designation || '',
    serviceItemName: record.serviceItemName || record.productDescription || '',
    serviceItemDescription: record.serviceItemDescription || record.productDescription || '',
    productSelectionStatus: record.productSelectionStatus || record.status || '',
    billingFrequency: record.billingFrequency || record.occurrence || '',
    gmCost: record.gmCost || record.filler || '',
    dealerPrice: record.dealerPrice || record.price || '',
    dealerName: record.dealerName || record.dealer || '',
    enrollmentStatusCd: record.enrollmentStatusCd || record.programStatusCd || record.programStatus || '',
    isTerminated: record.isTerminated ?? record.terminatedFlag ?? record.isTerminatedFlag ?? '',
    isBilling: record.isBilling ?? record.billingStatus ?? record.billingFlag ?? '',
    brandsDisplay: record.brandsDisplay || brands.display,
    brandsCodes: record.brandsCodes || brands.codes,
    productSelectionDate: selectionDateInfo.value,
    productSelectionDateDisplay: selectionDateInfo.display,
    productSelectionDateSort: selectionDateInfo.sort,
    goLiveDate: goLiveInfo.value,
    goLiveDateDisplay: goLiveInfo.display,
    goLiveDateSort: goLiveInfo.sort,
    effectiveDate: effectiveInfo.value,
    effectiveDateDisplay: effectiveInfo.display,
    effectiveDateSort: effectiveInfo.sort,
    cancelDate: cancelInfo.value,
    cancelDateDisplay: cancelInfo.display,
    cancelDateSort: cancelInfo.sort,
    billingStartDate: billingStartInfo.value,
    billingStartDateDisplay: billingStartInfo.display,
    billingStartDateSort: billingStartInfo.sort,
    billingEndDate: billingEndInfo.value,
    billingEndDateDisplay: billingEndInfo.display,
    billingEndDateSort: billingEndInfo.sort,
    vendorProvidedDate: vendorProvidedInfo.value,
    vendorProvidedDateDisplay: vendorProvidedInfo.display,
    vendorProvidedDateSort: vendorProvidedInfo.sort,
    terminationDate: terminationInfo.value,
    terminationDateDisplay: terminationInfo.display,
    terminationDateSort: terminationInfo.sort,
    enrollmentDate: enrollmentInfo.value,
    enrollmentDateDisplay: enrollmentInfo.display,
    enrollmentDateSort: enrollmentInfo.sort,
    optOutDate: optOutInfo.value,
    optOutDateDisplay: optOutInfo.display,
    optOutDateSort: optOutInfo.sort,
    sendTimestamp: sendTimestampInfo.value,
    sendTimestampDisplay: sendTimestampInfo.display,
    sendTimestampSort: sendTimestampInfo.sort,
  };
};

/**
 * Flatten ProductSelectionSend entries into grid-ready rows.
 * Handles legacy payloads that may not include program metadata.
 */
export const buildSendHistoryRows = (data) => {
  if (!Array.isArray(data)) return [];
  return data.flatMap((entry) => {
    const payload = Array.isArray(entry.payload) ? entry.payload : [];
    return payload.map((record, index) => {
      const sendTimestamp =
        entry.sendTimestamp ||
        entry.send_timestamp ||
        record.sendTimestamp ||
        record.send_timestamp ||
        entry.createdAt ||
        entry.created_at ||
        entry.updatedAt ||
        entry.updated_at ||
        entry.timestamp ||
        null;

      return normalizeSendHistoryRow({
        id: `${entry.id}-${index}`,
        ...record,
        bac: record.bac || '',
        programName:
          record.programName ||
          record.program ||
          CATEGORY_PROGRAM_MAP[record.categoryCd || record.categoryCode || ''] ||
          '',
        sendStatus: entry.status,
        sendStatusCode: entry.statusCode,
        sendTimestamp,
        snapshotStatus: entry.snapshotStatus || 'skipped',
        snapshotCapturedAt: entry.snapshotCapturedAt || null,
        snapshotError: entry.snapshotError || null,
        __sendEntry: entry,
        __recordIndex: index,
      });
    });
  });
};

const SendHistory = () => {
  const { useVmtSendHistory, useDeleteVmtSendHistory } = useApi();
  const { data, isLoading, isError, error, refetch, isFetching } = useVmtSendHistory();
  const deleteHistory = useDeleteVmtSendHistory();
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [confirmState, setConfirmState] = useState({
    open: false,
    ids: [],
    description: '',
  });

  const rawRows = useMemo(() => buildSendHistoryRows(data), [data]);
  const { rows, meta } = useEnrichedSendHistoryRows(rawRows);
  useEffect(() => {
    setSelectionModel((prev) => prev.filter((id) => rows.some((row) => row.id === id)));
  }, [rows]);

  const handleRowClick = useCallback((params) => {
    if (!params?.row?.__sendEntry) return;
    const { __sendEntry, ...rest } = params.row;
    setSelectedRow({
      entry: __sendEntry,
      record: rest,
    });
  }, []);

  const getEntryIdsFromRowIds = useCallback(
    (ids) => {
      const idSet = new Set();
      ids.forEach((rowId) => {
        const row = rows.find((item) => item.id === rowId);
        const entryId = row?.__sendEntry?.id;
        if (entryId) {
          idSet.add(entryId);
        }
      });
      return Array.from(idSet);
    },
    [rows]
  );

  const handleDeleteSelected = useCallback(() => {
    const entryIds = getEntryIdsFromRowIds(selectionModel);
    if (entryIds.length === 0) return;
    setConfirmState({
      open: true,
      ids: entryIds,
      description:
        entryIds.length === 1
          ? 'Delete this history entry?'
          : `Delete ${entryIds.length} history entries?`,
    });
  }, [selectionModel, getEntryIdsFromRowIds]);

  const handleClearAll = useCallback(() => {
    const allEntryIds = Array.from(
      new Set(rawRows.map((row) => row?.__sendEntry?.id).filter((id) => Boolean(id)))
    );
    if (allEntryIds.length === 0) return;
    setConfirmState({
      open: true,
      ids: allEntryIds,
      description: 'Clear all send history entries?',
    });
  }, [rawRows]);

  const closeConfirmDialog = useCallback(() => {
    if (deleteHistory.isPending) return;
    setConfirmState({ open: false, ids: [], description: '' });
  }, [deleteHistory.isPending]);

  const confirmDeletion = useCallback(async () => {
    if (confirmState.ids.length === 0) return;
    try {
      await deleteHistory.mutateAsync(confirmState.ids);
      setSelectionModel([]);
      await refetch();
    } catch (mutationError) {
      console.error('Failed to delete history entries', mutationError);
    } finally {
      setConfirmState({ open: false, ids: [], description: '' });
    }
  }, [confirmState.ids, deleteHistory, refetch]);

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title="Send History"
        subheader="Review previously submitted product selections."
        action={
          <Button variant="outlined" size="small" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      />
      <CardContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {isError && <Alert severity="error">{error?.message || 'Failed to load send history.'}</Alert>}
        {!isLoading && !isError && rows.length === 0 && (
          <Alert severity="info">No send history records found.</Alert>
        )}
        {!isLoading && !isError && rows.length > 0 && (
          <>
            {meta.usedCache && (
              <Box sx={{ mb: 1 }}>
                <Chip label="Pulled from cache" color="warning" size="small" />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={selectionModel.length === 0 || deleteHistory.isPending}
                onClick={handleDeleteSelected}
              >
                Delete Selected
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={rows.length === 0 || deleteHistory.isPending}
                onClick={handleClearAll}
              >
                Clear History
              </Button>
            </Box>
            <ProductSelectionsTable
              rows={rows}
              additionalColumns={SEND_HISTORY_ADDITIONAL_COLUMNS}
              additionalColumnsPlacement="start"
              onRowClick={handleRowClick}
              selectionModel={selectionModel}
              onSelectionModelChange={setSelectionModel}
            />
          </>
        )}
      </CardContent>
      <SendHistoryResponseModal
        open={Boolean(selectedRow)}
        onClose={() => setSelectedRow(null)}
        entry={selectedRow?.entry || null}
        record={selectedRow?.record || null}
      />
      <ConfirmDialog
        open={confirmState.open}
        title="Confirm Deletion"
        description={confirmState.description}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteHistory.isPending}
        onConfirm={confirmDeletion}
        onCancel={closeConfirmDialog}
      />
    </Card>
  );
};

export default SendHistory;
